import { Link, Navigate, useLocation } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import OppdagTab from "@/components/salgsmuligheter/OppdagTab";
import PipeTab from "@/components/salgsmuligheter/PipeTab";
import AgentsTab from "@/components/salgsmuligheter/agents/AgentsTab";
import LeadsTab from "@/components/salgsmuligheter/leads/LeadsTab";
import LeadListsTab from "@/components/salgsmuligheter/leads/LeadListsTab";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { useCanAccessMenu } from "@/hooks/use-menu-permissions";

const OPPDAG_PATH = "/salgsmuligheter";
const PIPE_PATH = "/salgsmuligheter/pipe";
const AGENTS_PATH = "/salgsmuligheter/agenter";
const LEADS_PATH = "/salgsmuligheter/leads";
const LEAD_LISTS_PATH = "/salgsmuligheter/leadlister";

/**
 * Salgsmuligheter (CRM fase 2). Oppdag og Pipe krever menynøkkelen
 * `salgsmuligheter` (gatet i App.tsx) og flagget `feature_crm_ko_pipe`.
 * Tab = rute. Agenter (CRM-3.3) ligger bak feature_crm_signaler. Leads (CRM-4)
 * har egen menynøkkel `salgsmuligheter.leads` og flagget feature_crm_leads_moter,
 * slik at rollen motebooker bare ser Leads-fanen. Leadlister (VIB-2988) har egen
 * menynøkkel `salgsmuligheter.leadlister` bak samme flagg.
 */
export default function SalgsmuligheterPage() {
  const koPipeFlag = useFeatureFlag("feature_crm_ko_pipe");
  const signaler = useFeatureFlag("feature_crm_signaler");
  const leadsFlag = useFeatureFlag("feature_crm_leads_moter");
  const canParent = useCanAccessMenu("salgsmuligheter");
  const canLeadsKey = useCanAccessMenu("salgsmuligheter.leads");
  const canListsKey = useCanAccessMenu("salgsmuligheter.leadlister");
  const { pathname } = useLocation();

  const enabled = canParent && koPipeFlag;
  const leads = leadsFlag && canLeadsKey;
  const leadLists = leadsFlag && canListsKey;
  const active = pathname.startsWith(LEADS_PATH)
    ? leads ? "leads" : null
    : pathname.startsWith(LEAD_LISTS_PATH)
      ? leadLists ? "leadlister" : null
      : !enabled
      ? null
      : pathname.startsWith(PIPE_PATH)
        ? "pipe"
        : pathname.startsWith(AGENTS_PATH) && signaler
          ? "agenter"
          : "oppdag";

  // Uten Oppdag og Pipe (møtebooker): Oppdag-ruten sender videre til Leads eller Leadlister.
  if (!enabled && !active && (leads || leadLists) && !pathname.startsWith(LEADS_PATH) && !pathname.startsWith(LEAD_LISTS_PATH)) {
    return <Navigate to={leads ? LEADS_PATH : LEAD_LISTS_PATH} replace />;
  }

  if (!active) {
    return (
      <div className="page-container animate-fade-in">
        <h1 className="page-title">Salgsmuligheter</h1>
        <p className="text-muted-foreground">Ikke tilgjengelig ennå.</p>
      </div>
    );
  }

  return (
    <div className="page-container animate-fade-in space-y-6">
      <h1 className="page-title">Salgsmuligheter</h1>
      <Tabs value={active}>
        <TabsList aria-label="Salgsmuligheter">
          {enabled && (
            <TabsTrigger value="oppdag" asChild>
              <Link to={OPPDAG_PATH}>Oppdag</Link>
            </TabsTrigger>
          )}
          {enabled && (
            <TabsTrigger value="pipe" asChild>
              <Link to={PIPE_PATH}>Pipe</Link>
            </TabsTrigger>
          )}
          {leads && (
            <TabsTrigger value="leads" asChild>
              <Link to={LEADS_PATH}>Leads</Link>
            </TabsTrigger>
          )}
          {leadLists && (
            <TabsTrigger value="leadlister" asChild>
              <Link to={LEAD_LISTS_PATH}>Leadlister</Link>
            </TabsTrigger>
          )}
          {enabled && signaler && (
            <TabsTrigger value="agenter" asChild>
              <Link to={AGENTS_PATH}>Agenter</Link>
            </TabsTrigger>
          )}
        </TabsList>
      </Tabs>
      {active === "leads" ? (
        <LeadsTab />
      ) : active === "leadlister" ? (
        <LeadListsTab />
      ) : active === "pipe" ? (
        <PipeTab />
      ) : active === "agenter" ? (
        <AgentsTab />
      ) : (
        <OppdagTab />
      )}
    </div>
  );
}
